import { UrlObject } from 'url';
import { Writable } from 'type-fest';
import logger from '@spa/lib/logger';
import { useMemo } from 'react';

export enum LinkTarget {
  SELF = '_self',
  TOP = '_top',
  PARENT = '_parent',
  BLANK = '_blank'
}

export interface LinkParam {
  readonly key: string;
  readonly value: string;
}

export interface BaseLink {
  readonly linkTitle: string | null;
  readonly linkParams: ReadonlyArray<LinkParam>;
  readonly anchorTarget: string | null;
  readonly target: LinkTarget;
  readonly onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

export interface LinkNormalizedSlug {
  readonly normalizedSlug: string;
}

export interface BrandLink extends BaseLink {
  readonly type: 'brand';
  readonly brand: LinkNormalizedSlug | null;
}

export interface CategoryLink extends BaseLink {
  readonly type: 'category';
  readonly category: LinkNormalizedSlug | null;
}

export interface ProductLinkProduct {
  readonly path: string;
}

export interface ProductLink extends BaseLink {
  readonly type: 'product';
  readonly product: ProductLinkProduct | null;
}

export interface ResolvedInternalLink extends BaseLink {
  readonly type: 'internal';
  readonly resolved: true;
  readonly path: string;
}

export interface UnresolvedInternalLink extends BaseLink {
  readonly type: 'internal';
  readonly resolved: false;
}

export interface PrivacySettingsLink extends BaseLink {
  readonly type: 'privacy-settings';
}

export interface AssetLinkAsset {
  readonly fileName: string;
  readonly fileSize: number;
  readonly mimeType: string;
  readonly url: string;
}

export interface ResolvedAssetLink extends BaseLink {
  readonly type: 'asset';
  readonly resolved: true;
  readonly asset: AssetLinkAsset;
}

export interface UnresolvedAssetLink extends BaseLink {
  readonly type: 'asset';
  readonly resolved: false;
}

export interface ExternalLink extends BaseLink {
  readonly type: 'external';
  readonly url: string;
}

export interface AnchorLink extends BaseLink {
  readonly type: 'anchor';
}

export type Link =
  | AnchorLink
  | BrandLink
  | CategoryLink
  | ResolvedInternalLink
  | UnresolvedInternalLink
  | ExternalLink
  | ProductLink
  | ResolvedAssetLink
  | UnresolvedAssetLink
  | PrivacySettingsLink;

export interface LinkAttributes {
  readonly href: UrlObject;
  readonly target: LinkTarget;
  readonly title?: string;
  readonly onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

const dummyHostname = 'https://localhost';

const parseUrl = (urlString: string, base?: string): URL | null => {
  let url: URL | null;
  try {
    url = new URL(decodeURIComponent(urlString), base);
  } catch {
    url = null;
  }
  return url;
};

const appendLinkParams = (url: URL | null, linkParams: ReadonlyArray<LinkParam>): string => {
  const searchParams = new URLSearchParams(url?.search ?? '');
  linkParams.forEach((param) => searchParams.set(param.key, param.value));
  return searchParams.toString();
};

const buildUnresolvedRef = (link: Link): UrlObject => {
  return {
    pathname: '',
    query: appendLinkParams(null, link.linkParams),
    hash: link.anchorTarget ?? ''
  };
};

const buildHref = (link: Link): UrlObject => {
  let result: UrlObject;
  switch (link.type) {
    case 'asset': {
      if (link.resolved) {
        const url = parseUrl(link.asset.url);
        if (url === null) {
          result = buildUnresolvedRef(link);
        } else {
          link.linkParams.forEach((linkParam) => {
            url.searchParams.append(linkParam.key, linkParam.value);
          });
          result = {
            protocol: url.protocol,
            hostname: url.hostname,
            port: url.port,
            pathname: url.pathname,
            query: url.searchParams.toString(),
            hash: link.anchorTarget ?? url.hash
          };
        }
      } else {
        result = buildUnresolvedRef(link);
      }
      break;
    }
    case 'brand': {
      const brand = link.brand;
      if (brand === null) {
        result = buildUnresolvedRef(link);
      } else {
        result = {
          pathname: `/b${brand.normalizedSlug}`,
          query: appendLinkParams(null, link.linkParams),
          hash: link.anchorTarget ?? ''
        };
      }
      break;
    }
    case 'category': {
      const category = link.category;
      if (category === null) {
        result = buildUnresolvedRef(link);
      } else {
        result = {
          pathname: `/c${category.normalizedSlug}`,
          query: appendLinkParams(null, link.linkParams),
          hash: link.anchorTarget ?? ''
        };
      }
      break;
    }
    case 'external': {
      const url = parseUrl(link.url);
      if (url === null) {
        const urlWithDummyHostname = parseUrl(link.url, dummyHostname);
        if (urlWithDummyHostname === null) {
          logger.warn({ link: { url: link.url } }, 'Could not parse external url.');
          result = buildUnresolvedRef(link);
        } else {
          link.linkParams.forEach((linkParam) => {
            urlWithDummyHostname.searchParams.append(linkParam.key, linkParam.value);
          });
          result = {
            pathname: urlWithDummyHostname.pathname,
            query: urlWithDummyHostname.searchParams.toString(),
            hash: link.anchorTarget ?? urlWithDummyHostname.hash
          };
        }
      } else {
        link.linkParams.forEach((linkParam) => {
          url.searchParams.append(linkParam.key, linkParam.value);
        });
        result = {
          protocol: url.protocol,
          hostname: url.hostname,
          port: url.port,
          pathname: url.pathname,
          query: url.searchParams.toString(),
          hash: link.anchorTarget ?? url.hash
        };
      }
      break;
    }
    case 'internal': {
      if (link.resolved) {
        const url = new URL(decodeURIComponent(link.path), dummyHostname);
        link.linkParams.forEach((linkParam) => {
          url.searchParams.append(linkParam.key, linkParam.value);
        });
        result = {
          pathname: url.pathname + (url.pathname.endsWith('/') ? '' : '/'),
          query: url.searchParams.toString(),
          hash: link.anchorTarget ?? url.hash
        };
      } else {
        result = buildUnresolvedRef(link);
      }
      break;
    }
    case 'product': {
      const product = link.product;
      if (product === null) {
        result = buildUnresolvedRef(link);
      } else {
        const url = new URL(decodeURIComponent(product.path), 'https://localhost');
        link.linkParams.forEach((linkParam) => {
          url.searchParams.append(linkParam.key, linkParam.value);
        });
        result = {
          pathname: url.pathname,
          query: url.searchParams.toString(),
          hash: link.anchorTarget ?? url.hash
        };
      }
      break;
    }
    default:
      result = buildUnresolvedRef(link);
  }
  return result;
};

export const convertLink = (link: Link, fallbackTitle?: string): LinkAttributes => {
  const attributes: Writable<LinkAttributes> = {
    href: buildHref(link),
    target: link.target,
    onClick: link.onClick
  };

  const sourceTitle = link.linkTitle;
  if (sourceTitle !== null) {
    attributes.title = sourceTitle;
  } else if (fallbackTitle !== undefined) {
    attributes.title = fallbackTitle;
  }

  return attributes;
};

export const useRequiredLinkAttributes = (link: Link): LinkAttributes => {
  return useMemo(() => {
    return convertLink(link);
  }, [link]);
};

const useLinkAttributes = (link: Link | null, fallbackTitle?: string): LinkAttributes | null => {
  return useMemo(() => {
    let targetAttributes: LinkAttributes | null = null;

    if (link !== null) {
      targetAttributes = convertLink(link, fallbackTitle);
    }

    return targetAttributes;
  }, [fallbackTitle, link]);
};

export default useLinkAttributes;
